import React from "react";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaSearch,
  FaFileAlt,
  FaBell,
  FaLandmark,
  FaHistory,
  FaRocket,
} from "react-icons/fa";

const logo =
  "https://jobx-image.s3.ap-south-1.amazonaws.com/Group+167+(3).svg";

/* Animation Variants */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 120, damping: 18 },
  },
};

const About = ({ onBack }) => {
  /* === Landing Page Color Theme === */
  const theme = {
    bg: "bg-[#FAFAFA]",
    textMain: "text-gray-900",
    textSub: "text-gray-500",

    card:
      "bg-white border border-gray-200 rounded-3xl transition-all duration-500 hover:shadow-2xl hover:border-gray-900 hover:-translate-y-2",

    btn:
      "bg-gray-900 hover:bg-black text-white transition-all shadow-lg active:scale-95",

    accentBg:
      "bg-white border border-gray-200 rounded-3xl shadow-xl",

    smoothEmphasis: "font-semibold text-gray-900",

    iconPrimary: "text-gray-900",
    iconMuted: "text-gray-500",

    upcomingBg: "bg-gray-50",
    upcomingBorder: "border-gray-300",
  };

  const features = [
    {
      icon: <FaSearch className={`text-4xl ${theme.iconPrimary}`} />,
      title: "AI-Powered Smart Match",
      text: "Advanced algorithms surface the most relevant opportunities based on skills, experience, and career intent — not just keywords.",
    },
    {
      icon: <FaFileAlt className={`text-4xl ${theme.iconPrimary}`} />,
      title: "ATS-Optimized Resumes",
      text: "Create clean, machine-readable resumes that pass Applicant Tracking Systems effortlessly.",
    },
    {
      icon: <FaLandmark className={`text-4xl ${theme.iconPrimary}`} />,
      title: "Verified Govt. Jobs",
      text: "Authentic government and PSU job listings updated regularly across India.",
    },
    {
      icon: <FaBell className={`text-4xl ${theme.iconPrimary}`} />,
      title: "Smart Alerts",
      text: "Real-time notifications when opportunities match your profile and preferences.",
    },
    {
      icon: <FaHistory className={`text-4xl ${theme.iconPrimary}`} />,
      title: "Search Continuity",
      text: "Your job search history is saved automatically so you can resume anytime.",
    },
    {
      icon: <FaRocket className={`text-4xl ${theme.iconMuted}`} />,
      title: "Upcoming Features",
      text: "Skill builders, interview prep tools, and personalized career guidance — launching soon.",
      comingSoon: true,
    },
  ];

  return (
    <div className={`min-h-screen ${theme.bg} overflow-hidden relative`}>
      
      {/* Background grid */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none
        bg-[linear-gradient(to_right,#f1f1f1_1px,transparent_1px),
            linear-gradient(to_bottom,#f1f1f1_1px,transparent_1px)]
        bg-[size:40px_40px]"
      />

      {/* Page Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-16"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full border-2 border-gray-900 font-bold text-gray-900 hover:bg-gray-900 hover:text-white transition-all"
          >
            <FaArrowLeft /> Back to Home
          </button>
        </motion.div>

        {/* Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.span
              variants={itemVariants}
              className="inline-block px-4 py-1.5 mb-8 text-xs font-black tracking-widest uppercase bg-gray-100 rounded-full"
            >
              About Jobx<span className="text-brand-saffron">Bharat</span>
            </motion.span>

            <motion.h1
              variants={itemVariants}
              className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-tight"
            >
              Building the future of <br /> hiring in India.
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className={`text-xl leading-relaxed ${theme.textSub}`}
            >
              Our mission is to{" "}
              <span className={theme.smoothEmphasis}>
                democratize job access
              </span>{" "}
              using intelligent technology, clean design, and a platform that
              stays{" "}
              <span className={theme.smoothEmphasis}>100% free</span> for job
              seekers.
            </motion.p>
          </motion.div>

          {/* Logo Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 80 }}
            className="flex justify-center"
          >
            <motion.div
              whileHover={{ y: -10, scale: 1.05 }}
              className="p-14 bg-white rounded-[3rem] border border-gray-200 shadow-2xl"
            >
              <img
                src={logo}
                alt="JobxBharat Logo"
                className="w-64 h-64 object-contain"
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Features */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((f, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className={`p-8 ${theme.card} ${
                f.comingSoon
                  ? `${theme.upcomingBg} ${theme.upcomingBorder}`
                  : ""
              }`}
            >
              <div className="mb-6">{f.icon}</div>
              <h3 className="text-xl font-black mb-3">
                {f.title}
              </h3>
              <p className={theme.textSub}>{f.text}</p>

              {f.comingSoon && (
                <span className="inline-block mt-5 px-4 py-1 text-xs font-bold border border-gray-400 rounded-full">
                  Coming Soon ....
                </span>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center"
        >
          <div className="max-w-4xl mx-auto p-16 bg-white rounded-[3rem] border border-gray-200 shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Ready to start your journey?
            </h2>
            <p className="text-xl text-gray-500 mb-10">
              Discover smarter job search with Jobx<span className="text-brand-saffron">Bharat</span>.
            </p>
            <button
              onClick={onBack}
              className={`px-12 py-5 rounded-2xl text-xl font-black ${theme.btn}`}
            >
              Start Searching Jobs →
            </button>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-24 text-center text-gray-400 font-medium">
          JobxBharat — Built with ❤️ in India 🇮🇳
        </div>
      </div>
    </div>
  );
};

export default About;
